// components/secure/PortalHome.js — the office home site.
//
// Built to the shape a Microsoft 365 home site uses, because that is the layout
// people already know how to read:
//
//   hero          one large featured tile and four smaller ones
//   dashboard     Viva-style action cards — a count, a line of context, a way in
//   news          card row with category, date and an excerpt
//   quick links   a dense icon grid, the way the Quick Links web part behaves
//   people        the directory strip, initials and a way to reach someone
//
// What it does NOT copy is the empty-state behaviour. A SharePoint page will
// happily render a web part with nothing in it; here, a block with no data does
// not render at all, so the page is never mostly furniture.
//
// Everything is permission-aware at the source: search scopes each block the way
// its own module does, the dashboard omits figures the user may not see, and
// tiles a role cannot open are not drawn.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import CollapsibleCard, { ExpandCollapseAll, CardHeading } from '../shared/CollapsibleCard';
import { countSummary, queueSummary } from '../../utils/collapsibleCards';

const isMB = (u) => !!u?.isManagingBroker || u?.role === 'admin';
const isStaff = (u) => isMB(u) || !!u?.staffRole;
const isAgent = (u) => !!u?.agentId;

// ---------------------------------------------------------------- hero
// One large tile, four small. The large one is where the office is working now.
const HERO = [
    { to: '/dashboard/transactions', label: 'Transactions', blurb: 'Every deal, from listing to close',
      tone: 'from-slate-800 to-slate-900', large: true },
    { to: '/dashboard/clients', label: 'Clients', blurb: 'Buyers, sellers, landlords, tenants', tone: 'from-slate-700 to-slate-900' },
    { to: '/dashboard/pm-leasing', label: 'Leasing & screening', blurb: 'Applications and tenancies', tone: 'from-sky-700 to-sky-900', staff: true },
    { to: '/dashboard/zoning-library', label: 'Zoning library', blurb: 'What each town permits', tone: 'from-emerald-700 to-emerald-900', staff: true },
    { to: '/dashboard/compliance', label: 'Compliance hub', blurb: 'Duties, disclosures, deadlines', tone: 'from-amber-600 to-amber-800' },
    // Agent logins (24 Oct 2026): their two searches in place of the staff-only tiles.
    { to: '/dashboard/property-search', label: 'Property search', blurb: 'Our listings and the MLS index', tone: 'from-sky-700 to-sky-900', agentOnly: true },
    { to: '/dashboard/property-search?tab=zoning', label: 'Zoning search', blurb: 'What can go where, by business', tone: 'from-emerald-700 to-emerald-900', agentOnly: true },
];

// ---------------------------------------------------------------- quick links
const LINKS = [
    { to: '/dashboard/properties', label: 'Properties', icon: '▦' },
    { to: '/dashboard/showings', label: 'Showings', icon: '◷' },
    { to: '/dashboard/deal-compliance', label: 'Required docs', icon: '☑' },
    { to: '/dashboard/document-library', label: 'Documents', icon: '🗀' },
    { to: '/dashboard/forms-cabinet', label: 'Forms', icon: '✎' },
    { to: '/dashboard/policy-center', label: 'Policies', icon: '§' },
    { to: '/dashboard/office-directory', label: 'Directory', icon: '☰' },
    { to: '/dashboard/mailroom', label: 'Mailroom', icon: '✉' },
    { to: '/dashboard/my-office', label: 'My office', icon: '⌂' },
    { to: '/dashboard/file-cabinet', label: 'My files', icon: '🗄' },
    { to: '/dashboard/tasks', label: 'Tasks', icon: '✓' },
    { to: '/dashboard/license-tracking', label: 'Licence & CE', icon: '★' },
    { to: '/dashboard/pm-portfolio', label: 'PM portfolio', icon: '⌸', staff: true },
    { to: '/dashboard/pm-trust-accounts', label: 'Trust accounts', icon: '⚖', staff: true },
    { to: '/dashboard/escrow', label: 'Escrow', icon: '$', mb: true },
    { to: '/dashboard/accounting', label: 'Accounting', icon: '≡', mb: true },
    { to: '/dashboard/agents', label: 'Licensees', icon: '☺', mb: true },
    { to: '/dashboard/retention', label: 'Retention', icon: '⏳', mb: true },
];

// ---------------------------------------------------------------- dashboard cards
const CARDS = [
    { key: 'openTransactions', title: 'Open deals', to: '/dashboard/transactions',
      line: 'in progress right now', tone: 'bg-indigo-50 text-indigo-900 border-indigo-200' },
    { key: 'docsOutstanding', title: 'Documents outstanding', to: '/dashboard/deal-compliance',
      line: 'required and not yet collected', tone: 'bg-amber-50 text-amber-900 border-amber-200' },
    { key: 'applicationsToScreen', title: 'Applications to screen', to: '/dashboard/pm-leasing',
      line: 'waiting on a decision', tone: 'bg-sky-50 text-sky-900 border-sky-200' },
    { key: 'adverseActionDue', title: 'FCRA notices due', to: '/dashboard/pm-leasing',
      line: 'a consumer report was relied on', tone: 'bg-rose-50 text-rose-900 border-rose-200' },
    { key: 'pmDeadlines', title: 'Deadlines in 14 days', to: '/dashboard/pm-deadlines',
      line: 'deposits, notices, renewals', tone: 'bg-amber-50 text-amber-900 border-amber-200' },
    { key: 'newSiteSearchFinds', title: 'New site-search finds', to: '/dashboard/clients',
      line: 'found in the last three days', tone: 'bg-emerald-50 text-emerald-900 border-emerald-200' },
    { key: 'licensesExpiring', title: 'Licences expiring', to: '/dashboard/license-tracking',
      line: 'within 60 days', tone: 'bg-rose-50 text-rose-900 border-rose-200' },
    { key: 'newRecruitApplications', title: 'New recruiting applications', to: '/dashboard/recruiting',
      line: 'agents and staff who applied to join', tone: 'bg-indigo-50 text-indigo-900 border-indigo-200' },
];

const initials = (a, b) => `${(a || '?')[0] || ''}${(b || '')[0] || ''}`.toUpperCase();
const fmtDue = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
const titleish = (s) => (s || '').replace(/_/g, ' ');

const SearchResults = ({ results, onClose }) => (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
            <span className="text-xs uppercase tracking-wider text-slate-500">Results for “{results.query}”</span>
            <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800">Clear</button>
        </div>
        {results.groups.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-500">Nothing matches that.</p>
        ) : (
            <div className="grid gap-x-8 gap-y-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                {results.groups.map((g) => (
                    <div key={g.label}>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g.label}</p>
                        <ul className="space-y-1">
                            {g.rows.map((r) => (
                                <li key={`${g.label}-${r.id}`} className="leading-tight">
                                    {r.href
                                        ? <a href={r.href} target="_blank" rel="noreferrer" className="text-sm text-indigo-700 hover:underline">{r.title}</a>
                                        : <Link to={r.to} className="text-sm text-indigo-700 hover:underline">{r.title}</Link>}
                                    {r.sub && <span className="block text-xs text-slate-400">{r.sub}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        )}
    </div>
);

const PortalHome = ({ user, announcements = [], articles = [], news = [], links = [], onOpenAnnouncement, children }) => {
    const api = useApiClient();
    const [q, setQ] = useState('');
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [queue, setQueue] = useState({});
    const [extras, setExtras] = useState({ people: [], deadlines: [], activity: [] });
    const timer = useRef(null);

    useEffect(() => {
        api.get('/api/intranet/work-queue').then(setQueue).catch(() => {});
        api.get('/api/intranet/portal-extras').then(setExtras).catch(() => {});
    }, [api]);

    // Debounced: a box that fires per keystroke hammers the database from every
    // open tab in the office.
    const runSearch = useCallback((value) => {
        clearTimeout(timer.current);
        if (value.trim().length < 2) { setResults(null); return; }
        timer.current = setTimeout(async () => {
            setSearching(true);
            try { setResults(await api.get(`/api/intranet/search?q=${encodeURIComponent(value)}`)); }
            catch { setResults(null); } finally { setSearching(false); }
        }, 250);
    }, [api]);

    const hero = HERO.filter((h) => (!h.staff || isStaff(user)) && (!h.mb || isMB(user)) && (!h.agentOnly || (isAgent(user) && !isStaff(user))));
    const quick = LINKS.filter((l) => (!l.staff || isStaff(user)) && (!l.mb || isMB(user)) && (!l.agent || isAgent(user)));
    const cards = CARDS.filter((c) => queue[c.key] > 0);
    const lead = announcements.find((a) => a.is_pinned) || announcements[0];
    const rest = announcements.filter((a) => a !== lead).slice(0, 4);

    return (
        <div className="space-y-8">
            {/* ---------------------------------------------- search band */}
            <div className="rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-7 text-white">
                <p className="text-xs uppercase tracking-[0.15em] text-slate-300">Business Real Estate Corp</p>
                <h1 className="mt-1 text-2xl font-bold">Office portal</h1>
                <div className="mt-4">
                    <input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }}
                        placeholder="Search clients, properties, deals, licensees, forms, towns…"
                        className="w-full rounded-lg border-0 px-4 py-3 text-base text-slate-900 shadow-sm
                                   placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <p className="mt-1.5 text-xs text-slate-400">
                        {searching ? 'Searching…' : 'Everything you are allowed to see, in one box.'}
                    </p>
                </div>
            </div>

            {results && <SearchResults results={results} onClose={() => { setQ(''); setResults(null); }} />}

            <ExpandCollapseAll className="-mt-5" />

            {/* ---------------------------------------------- hero */}
            <CollapsibleCard id="home-areas" title="Main areas" summary={hero.map((h) => h.label).join(' · ')}>
            <div className="grid gap-3 md:grid-cols-4 md:grid-rows-2">
                {hero.map((h, i) => (
                    <Link key={h.to} to={h.to}
                          className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${h.tone} p-5 text-white
                                      transition hover:brightness-110
                                      ${i === 0 ? 'md:col-span-2 md:row-span-2 md:p-7' : ''}`}>
                        <div className={`font-bold ${i === 0 ? 'text-2xl' : 'text-base'}`}>{h.label}</div>
                        <div className={`mt-1 text-white/80 ${i === 0 ? 'text-sm' : 'text-xs'}`}>{h.blurb}</div>
                        <span className="absolute bottom-4 right-5 text-white/40 transition group-hover:text-white/80">→</span>
                    </Link>
                ))}
            </div>
            </CollapsibleCard>

            {/* ---------------------------------------------- dashboard cards */}
            {cards.length > 0 && (
                <CollapsibleCard id="home-waiting" title="Waiting for you" summary={queueSummary(cards, queue)}
                                 defaultOpen={queue.adverseActionDue > 0} tone={queue.adverseActionDue > 0 ? 'text-red-700 font-semibold' : undefined}>
                <section>
                    <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        Waiting for you
                    </CardHeading>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {cards.map((c) => (
                            <Link key={c.key} to={c.to}
                                  className={`rounded-lg border p-4 transition hover:shadow-sm ${c.tone}`}>
                                <div className="text-3xl font-bold tabular-nums leading-none">{queue[c.key]}</div>
                                <div className="mt-1 text-sm font-semibold">{c.title}</div>
                                <div className="text-xs opacity-70">{c.line}</div>
                            </Link>
                        ))}
                    </div>
                </section>
                </CollapsibleCard>
            )}

            {/* ---------------------------------------------- news + notices */}
            {(lead || news.length > 0) && (
                <section className="grid gap-6 lg:grid-cols-3">
                    <CollapsibleCard id="home-news" title="Office news" summary={lead ? lead.title : ''} className="lg:col-span-2">
                    <div className="lg:col-span-2">
                        <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Office news
                        </CardHeading>
                        {lead && (
                            <button onClick={() => onOpenAnnouncement?.(lead)}
                                    className="mb-3 w-full rounded-lg border border-slate-200 bg-white p-5 text-left hover:border-indigo-300">
                                <div className="flex flex-wrap items-center gap-2">
                                    {!!lead.is_pinned && (
                                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                                            Pinned
                                        </span>
                                    )}
                                    <span className="text-[11px] uppercase tracking-wider text-slate-400">{lead.category}</span>
                                </div>
                                <h3 className="mt-1 text-lg font-bold text-slate-900">{lead.title}</h3>
                                <p className="mt-1 line-clamp-2 text-sm text-slate-600"
                                   dangerouslySetInnerHTML={{ __html: (lead.body_html || '').replace(/<[^>]+>/g, ' ').slice(0, 220) }} />
                            </button>
                        )}
                        {rest.length > 0 && (
                            <div className="grid gap-3 sm:grid-cols-2">
                                {rest.map((a) => (
                                    <button key={a.announcement_id} onClick={() => onOpenAnnouncement?.(a)}
                                            className="rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-indigo-300">
                                        <div className="text-[11px] uppercase tracking-wider text-slate-400">{a.category}</div>
                                        <div className="text-sm font-semibold text-slate-800">{a.title}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    </CollapsibleCard>

                    {/* what is due, and what the office has been doing */}
                    <div className="space-y-4">
                        {extras.deadlines?.length > 0 && (
                            <CollapsibleCard id="home-deadlines" title="Next deadlines"
                                             summary={`${extras.deadlines.length} · next ${fmtDue(extras.deadlines[0].due_on)}`}>
                            <div>
                                <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                    Next deadlines
                                </CardHeading>
                                <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                                    {extras.deadlines.map((d) => (
                                        <Link key={d.deadline_id} to="/dashboard/pm-deadlines"
                                              className="flex items-start gap-3 px-3 py-2 hover:bg-slate-50">
                                            <span className="mt-0.5 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700">
                                                {fmtDue(d.due_on)}
                                            </span>
                                            <span className="text-sm text-slate-700">
                                                {titleish(d.obligation)}
                                                {d.property_name && <span className="block text-xs text-slate-400">{d.property_name}</span>}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                            </CollapsibleCard>
                        )}
                        {extras.activity?.length > 0 && (
                            <CollapsibleCard id="home-activity" title="Site search activity" summary={countSummary(extras.activity.length, 'recent')}>
                            <div>
                                <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                    Site search activity
                                </CardHeading>
                                <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 text-sm">
                                    {extras.activity.map((a, i) => (
                                        <li key={i} className="px-3 py-2">
                                            <span className="font-medium text-slate-700">{titleish(a.event)}</span>
                                            <span className="text-slate-500"> · {a.client_name}</span>
                                            {a.detail && <span className="block text-xs text-slate-400">{a.detail}</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            </CollapsibleCard>
                        )}
                    </div>
                </section>
            )}

            {/* ---------------------------------------------- quick links */}
            <CollapsibleCard id="home-quick-links" title="Quick links" summary={`${quick.length + Math.min(links.length, 10)} links`}>
            <section>
                <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Quick links</CardHeading>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                    {quick.map((l) => (
                        <Link key={l.to + l.label} to={l.to}
                              className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-3
                                         text-center hover:border-indigo-300 hover:shadow-sm">
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                                {l.icon}
                            </span>
                            <span className="text-xs font-medium text-slate-700">{l.label}</span>
                        </Link>
                    ))}
                </div>
                {links.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {links.slice(0, 10).map((l) => (
                            <a key={l.link_id} href={l.url} target="_blank" rel="noreferrer"
                               className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:border-indigo-300">
                                {l.label} ↗
                            </a>
                        ))}
                    </div>
                )}
            </section>
            </CollapsibleCard>

            {/* ---------------------------------------------- people */}
            {extras.people?.length > 0 && (
                <CollapsibleCard id="home-people" title="The office" summary={countSummary(extras.people.length, extras.people.length === 1 ? 'person' : 'people')}>
                <section>
                    <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        The office
                    </CardHeading>
                    <div className="flex flex-wrap gap-2">
                        {extras.people.map((p) => (
                            <Link key={p.agent_id} to={`/dashboard/agents/${p.agent_id}`}
                                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-indigo-300">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-xs font-bold text-white">
                                    {initials(p.first_name, p.last_name)}
                                </span>
                                <span className="leading-tight">
                                    <span className="block text-sm font-medium text-slate-800">
                                        {p.first_name} {p.last_name}
                                    </span>
                                    <span className="block text-[11px] text-slate-400">
                                        {p.is_managing_broker ? 'Managing broker' : (p.phone_number || p.email || 'Licensee')}
                                    </span>
                                </span>
                            </Link>
                        ))}
                    </div>
                </section>
                </CollapsibleCard>
            )}

            {/* ---------------------------------------------- guides, then the rest */}
            {articles.length > 0 && (
                <CollapsibleCard id="home-guides" title="Recently updated guides" summary={countSummary(Math.min(articles.length, 8), 'updated')}>
                <section>
                    <CardHeading className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        Recently updated guides
                    </CardHeading>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                        {articles.slice(0, 8).map((a) => (
                            <div key={a.article_id} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                <div className="text-[11px] uppercase tracking-wider text-slate-400">{a.category}</div>
                                <div className="text-sm font-medium text-slate-800">{a.title}</div>
                            </div>
                        ))}
                    </div>
                </section>
                </CollapsibleCard>
            )}

            {children}
        </div>
    );
};

export default PortalHome;
